import { useCallback } from 'react';
import { ThunkDispatch } from 'redux-thunk';
import { 
  createCQATransaction as createCQATransactionAction,
  addCode as addCodeAction,
  gradeUpdate as gradeUpdateAction
} from '../actions/grade-bench-panel-actions';

/**
 * Hook for creating CQA transactions
 * Provides a reusable way to create transactions for invoices
 * 
 * @param dispatch - Redux dispatch function
 * @returns Object with createTransaction function
 */
export const useCreateTransaction = (dispatch: ThunkDispatch<any, any, any>) => {
  /**
   * Create a transaction for an invoice
   * @param invoiceId - The ID of the invoice to create a transaction for
   * @returns Promise that resolves to the created transaction or null if it fails
   */
  const createTransaction = useCallback(
    async (invoiceId: number): Promise<Accelerate.CQATransaction | null> => {
      try {
        const result = await dispatch(
          createCQATransactionAction({ invoiceId })
        ).unwrap();
        // The dispatch returns the result from the async action
        return result as Accelerate.CQATransaction | null;
      } catch (error) {
        console.error('Error creating transaction:', error);
        return null;
      }
    },
    [dispatch]
  );

  return {
    createTransaction
  };
};

/**
 * Hook for creating a CQA transaction and adding a code in one operation
 * Always creates a new transaction first, then adds the code to it
 * 
 * @param dispatch - Redux dispatch function
 * @returns Object with createTransactionAndAddCode function
 */
export const useCreateTransactionAndAddCode = (dispatch: ThunkDispatch<any, any, any>) => {
  /**
   * Create a transaction and add a code to it
   * @param payload - The code add action payload with invoice context
   * @returns Promise that resolves to the code add payload or null if it fails
   */
  const createTransactionAndAddCode = useCallback(
    async (payload: Accelerate.CodeAddActionPayload): Promise<Accelerate.CodeAddActionPayload | null> => {
      const { context } = payload;
      const invoice = context?.invoice as Accelerate.CQAInvoice;

      if (!invoice?.id) {
        console.error('Invoice ID is required to create transaction');
        return null;
      }

      // Always create a new transaction first
      const transaction = await dispatch(
        createCQATransactionAction({ invoiceId: invoice.id })
      ).unwrap();

      if (!transaction) {
        // Transaction creation failed - Redux store will handle error messages
        return null;
      }

      // Add the code with the newly created transaction
      const codePayload: Accelerate.CodeAddActionPayload = {
        ...payload,
        context: {
          ...context,
          transaction
        }
      };

      return await dispatch(addCodeAction(codePayload)).unwrap();
    },
    [dispatch]
  );

  return {
    createTransactionAndAddCode
  };
};

/**
 * Hook for updating code assessment or role
 * Provides a reusable way to update code assessments (assessment/role fields)
 * 
 * @param dispatch - Redux dispatch function
 * @returns Object with updateCodeAssessment function
 */
export const useCodeAssessmentUpdate = (dispatch: ThunkDispatch<any, any, any>) => {
  /**
   * Update a code's assessment or role
   * @param changeEvent - The grade bench select event with code and field information
   * @returns Promise that resolves to null (gradeUpdate returns null)
   */
  const updateCodeAssessment = useCallback(
    async (changeEvent: CMxCommonApp.GradeBenchSelectEvent): Promise<void> => {
      try {
        await dispatch(gradeUpdateAction(changeEvent)).unwrap();
      } catch (error) {
        console.error('Error updating code assessment:', error);
        throw error;
      }
    },
    [dispatch]
  );

  return {
    updateCodeAssessment
  };
};

